import React, { useState } from "react";
import { Link, NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard, BookOpen, Warehouse as WarehouseIcon, PackageOpen, ArrowLeftRight,
  Users, Truck, CalendarCheck, FileText, Undo2, BarChart3, Settings, LogOut, Search, BookMarked, Upload, Menu, X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";

const nav = [
  { to: "/", label: "Panel", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/catalogo", label: "Catálogo", icon: BookOpen, testid: "nav-catalog" },
  { to: "/almacen", label: "Almacén", icon: WarehouseIcon, testid: "nav-warehouse" },
  { to: "/deposito", label: "Depósito", icon: PackageOpen, testid: "nav-consignment" },
  { to: "/movimientos", label: "Movimientos", icon: ArrowLeftRight, testid: "nav-movements" },
  { to: "/liquidaciones", label: "Liquidaciones", icon: CalendarCheck, testid: "nav-settlements" },
  { to: "/facturas", label: "Facturas", icon: FileText, testid: "nav-invoices" },
  { to: "/devoluciones", label: "Devoluciones", icon: Undo2, testid: "nav-returns" },
  { to: "/clientes", label: "Clientes", icon: Users, testid: "nav-customers" },
  { to: "/distribuidores", label: "Distribuidores", icon: Truck, testid: "nav-distributors" },
  { to: "/informes", label: "Informes", icon: BarChart3, testid: "nav-reports" },
  { to: "/importar", label: "Importar", icon: Upload, testid: "nav-importer" },
  { to: "/documentacion", label: "Documentación", icon: BookMarked, testid: "nav-docs" },
  { to: "/configuracion", label: "Configuración", icon: Settings, testid: "nav-settings" },
];

function SidebarContent({ user, logout, onNavClick }) {
  return (
    <>
      <Link
        to="/"
        onClick={onNavClick}
        className="px-6 py-6 border-b border-line/80 hover:bg-bg/60 transition-colors"
        data-testid="sidebar-brand"
      >
        <div className="font-heading text-2xl text-ink leading-none">Editorial</div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-ink2 mt-1">Gestión Interna</div>
      </Link>
      <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
        {nav.map(n => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={onNavClick}
            data-testid={n.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-body transition-colors ${
                isActive ? "bg-white text-ink border border-line" : "text-ink2 hover:text-ink hover:bg-white/60"
              }`
            }
          >
            <n.icon size={16} strokeWidth={1.5} />
            <span>{n.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-line/80">
        <div className="flex items-center justify-between px-2 py-1.5">
          <div className="min-w-0">
            <div className="text-xs text-ink2 truncate">Conectada</div>
            <div className="text-sm text-ink font-medium truncate" data-testid="sidebar-user-email">
              {user?.email || "—"}
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="p-2 rounded-sm text-ink2 hover:text-brand hover:bg-white transition-colors"
            title="Salir"
          >
            <LogOut size={16} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  function onSearch(e) {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    navigate(`/catalogo?q=${encodeURIComponent(term)}`);
  }

  return (
    <div className="min-h-screen flex bg-bg">
      {/* Sidebar desktop */}
      <aside className="w-64 shrink-0 border-r border-line bg-sidebar hidden md:flex md:flex-col" data-testid="sidebar">
        <SidebarContent user={user} logout={logout} onNavClick={undefined} />
      </aside>

      {/* Sidebar móvil — overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-sidebar border-r border-line flex flex-col shadow-xl">
            <SidebarContent user={user} logout={logout} onNavClick={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-line bg-bg/80 backdrop-blur-sm sticky top-0 z-30 flex items-center gap-3 px-4 md:px-8">
          {/* Botón hamburguesa móvil */}
          <button
            className="md:hidden p-2 rounded-sm text-ink2 hover:text-ink hover:bg-white transition-colors"
            onClick={() => setMobileOpen(true)}
            aria-label="Abrir menú"
          >
            <Menu size={20} strokeWidth={1.5} />
          </button>

          <form onSubmit={onSearch} className="flex-1 max-w-xl" data-testid="global-search-form">
            <div className="relative">
              <Search size={15} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink2" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar por título, ISBN o autor…"
                data-testid="global-search-input"
                className="w-full bg-white border border-line rounded-sm pl-9 pr-3 py-2 text-sm font-body focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand transition"
              />
            </div>
          </form>
          <div className="hidden md:flex items-center gap-3 ml-6 text-xs text-ink2 font-mono">
            <span>ES · EUR · IVA 4%</span>
          </div>
        </header>
        <main className="flex-1 px-4 md:px-8 py-8 animate-fade-up">
          <Outlet />
        </main>
      </div>

      <Toaster richColors position="top-right" />

      {/* Badge Tuadministrativo */}
      <a
        href="https://www.tuadministrativo.com"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-4 right-4 z-50 flex items-center gap-2 bg-white border border-line rounded-sm shadow-md px-3 py-2 hover:shadow-lg transition-shadow"
        title="Hecho por Tuadministrativo.com"
      >
        <img src="/tuadministrativo-logo.png" alt="Tuadministrativo.com" className="h-5 w-auto" />
        <span className="text-[10px] font-body text-ink2 whitespace-nowrap">Hecho por tuadministrativo.com</span>
      </a>
    </div>
  );
}
